import type {
  CrewMembershipStatus,
  CrewRole,
  ExpeditionKind,
  ExpeditionStatus,
  SeaState,
  VesselRole,
  WindDirection,
} from "~/graphql/generated";

// ── Status ────────────────────────────────────────────────────────────────────

export const STATUS_CLS: Record<ExpeditionStatus, string> = {
  PLANNING: "badge badge-planning",
  OPEN: "badge badge-open",
  FULLY_BOOKED: "badge badge-open",
  ACTIVE: "badge badge-active badge-live",
  COMPLETED: "badge badge-completed",
  CANCELLED: "badge badge-cancelled",
};

export const STATUS_LABEL: Record<ExpeditionStatus, string> = {
  PLANNING: "Planning",
  OPEN: "Open",
  FULLY_BOOKED: "Fully Booked",
  ACTIVE: "Live",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

export const STATUS_BAR: Record<ExpeditionStatus, string> = {
  PLANNING: "var(--neutral-300)",
  OPEN: "var(--color-primary)",
  FULLY_BOOKED: "var(--color-accent)",
  ACTIVE: "var(--color-success)",
  COMPLETED: "var(--color-purple)",
  CANCELLED: "var(--color-danger)",
};

// ── Expedition kind ───────────────────────────────────────────────────────────

export const KIND_LABEL: Record<ExpeditionKind, string> = {
  OFFSHORE_PASSAGE: "Offshore Passage",
  COASTAL_CRUISE: "Coastal Cruise",
  DELIVERY: "Delivery",
  SAIL_TRAINING: "Sail Training",
  RACE: "Race",
  RALLY: "Rally",
  EXPEDITION: "Expedition",
};

// ── Crew ──────────────────────────────────────────────────────────────────────

export const CREW_ROLE_LABEL: Record<CrewRole, string> = {
  SKIPPER: "Skipper",
  FIRST_MATE: "First Mate",
  CREW: "Crew",
  STUDENT: "Student",
  OBSERVER: "Observer",
};

export const CREW_STATUS_LABEL: Record<CrewMembershipStatus, string> = {
  CONFIRMED: "Confirmed",
  PENDING: "Pending",
  WAITLISTED: "Waitlisted",
  CANCELLED: "Cancelled",
  NO_SHOW: "No Show",
  DISEMBARKED_EARLY: "Disembarked Early",
};

export const CREW_STATUS_CLS: Record<CrewMembershipStatus, string> = {
  CONFIRMED: "badge badge-active",
  PENDING: "badge badge-planning",
  WAITLISTED: "badge badge-neutral",
  CANCELLED: "badge badge-cancelled",
  NO_SHOW: "badge badge-cancelled",
  DISEMBARKED_EARLY: "badge badge-neutral",
};

export const ROLE_CLS: Record<string, string> = {
  SKIPPER: "role-pill role-skipper",
  FIRST_MATE: "role-pill role-firstmate",
  CREW: "role-pill role-crew",
  STUDENT: "role-pill role-student",
  OBSERVER: "role-pill role-observer",
};

// ── Vessel ────────────────────────────────────────────────────────────────────

export const VESSEL_ROLE_LABEL: Record<VesselRole, string> = {
  LEAD_VESSEL: "Lead Vessel",
  SUPPORT: "Support",
  SAFETY: "Safety",
  CHARTER: "Charter",
};

// ── State advance ─────────────────────────────────────────────────────────────

export type StateAdvance = {
  label: string;
  title: string;
  body: string;
  action: string;
  cls: string;
  nextStatus: ExpeditionStatus;
} | null;

export const STATE_ADVANCE: Record<ExpeditionStatus, StateAdvance> = {
  PLANNING: {
    label: "Next step",
    title: "Open for registration",
    body: "Once the route and vessel are confirmed, open this expedition so crew can apply.",
    action: "Open Expedition",
    cls: "state-advance",
    nextStatus: "OPEN",
  },
  OPEN: {
    label: "Next step",
    title: "Start the expedition",
    body: "Crew is confirmed and departure day has arrived. Mark the expedition as active.",
    action: "Start Expedition",
    cls: "state-advance",
    nextStatus: "ACTIVE",
  },
  FULLY_BOOKED: {
    label: "Next step",
    title: "Start the expedition",
    body: "All berths are filled. Mark the expedition as active when you depart.",
    action: "Start Expedition",
    cls: "state-advance",
    nextStatus: "ACTIVE",
  },
  ACTIVE: {
    label: "Underway",
    title: "Mark as completed",
    body: "When the boat is back in port and all crew have disembarked, close the expedition. This is permanent — certifications and sailing miles can be issued after completion.",
    action: "Complete Expedition",
    cls: "state-advance",
    nextStatus: "COMPLETED",
  },
  COMPLETED: null,
  CANCELLED: {
    label: "Cancelled expedition",
    title: "Reopen for rescheduling",
    body: "Return this expedition to Planning so it can be rescheduled. Previous crew records are preserved.",
    action: "Return to Planning",
    cls: "state-advance state-advance--warn",
    nextStatus: "PLANNING",
  },
};

// ── Avatar palette ────────────────────────────────────────────────────────────

export const AVATAR_PALETTE: { bg: string; color: string }[] = [
  { bg: "#e0f2fe", color: "#0369a1" },
  { bg: "#d1fae5", color: "#065f46" },
  { bg: "#ede9fe", color: "#5b21b6" },
  { bg: "#fce7f3", color: "#9d174d" },
  { bg: "#fef3c7", color: "#92400e" },
  { bg: "#fee2e2", color: "#991b1b" },
];

// ── Logbook ───────────────────────────────────────────────────────────────────

export const SEA_STATE_LABEL: Record<SeaState, string> = {
  CALM: "Calm",
  SLIGHT: "Slight",
  MODERATE: "Moderate",
  ROUGH: "Rough",
  VERY_ROUGH: "Very Rough",
  HIGH: "High",
};

// 3×3 compass grid: NW N NE / W · E / SW S SE
export const COMPASS_GRID: (WindDirection | null)[] = ["NW", "N", "NE", "W", null, "E", "SW", "S", "SE"];

// ── Currencies (expenses) ─────────────────────────────────────────────────────

export const CURRENCIES = ["USD", "EUR", "GBP", "HRK", "AUD", "CAD", "NOK"];
